import { WordConvertListSchema, WordAttachNumberingSchema, WordEditTableSchema } from './word-structure-formatting.js';
/** Canvas vocabulary, with explicit Office addresses and supported Word capabilities. */
import { z } from 'zod';
import { WordFormattingPlaceSchema } from './word-format-place.js';
import {
  coerceCanvasToolArgs,
  FormatTextInputSchema,
  FormatParagraphsInputSchema,
  SetPageLayoutInputSchema,
} from './canvas/actionCatalog.js';

const index = z.number().int().nonnegative().safe();
const all = z.object({ kind: z.literal('all'), in: WordFormattingPlaceSchema.optional() }).strict();
const indices = z.object({ kind: z.literal('indices'), in: WordFormattingPlaceSchema.optional(), paragraphIndices: z.array(index).min(1).refine((values) => new Set(values).size === values.length, 'paragraphIndices must contain each Word index once') }).strict();
const range = z.object({ kind: z.literal('range'), in: WordFormattingPlaceSchema.optional(), from: index, to: index }).strict();
const text = z.object({ kind: z.literal('text'), in: WordFormattingPlaceSchema.optional(), paragraphIndex: index.optional(), text: z.union([z.string().min(1), z.array(z.string().min(1)).min(1).max(50)]) }).strict();

/** Zero-based Office indices include empty paragraphs; DOCX reading ranks do not. */
const WordParagraphTargetSchema = z.discriminatedUnion('kind', [all, indices, range]);
const WordFormattingTargetSchema = z.discriminatedUnion('kind', [all, indices, range, text]);
export type WordParagraphTarget = z.infer<typeof WordParagraphTargetSchema>;

const WordFormatTextSchema = FormatTextInputSchema.omit({ target: true }).extend({
  target: WordFormattingTargetSchema,
}).strict();

const WordFormatParagraphsSchema = FormatParagraphsInputSchema.omit({ target: true }).extend({
  target: WordParagraphTargetSchema,
  // The reference assignation uses a 1pt gap; OOXML stores whole points and eighth-point widths.
  // Source: https://learn.microsoft.com/en-us/dotnet/api/documentformat.openxml.wordprocessing.bottomborder (verified 2026-10-13).
  borderSpacePt: z.number().int().nonnegative().optional(),
  // Not multipleOf(0.125): the DOCX reader shows 3/8 pt rounded as 0.38, and the
  // add-in rounds to the nearest eighth anyway (#2172 review).
  borderWidthPt: FormatParagraphsInputSchema.shape.borderWidthPt.unwrap().describe('Épaisseur en points, arrondie au huitième de point par Word.').optional(),
  // With or without '#', like shading: both spellings reach the same OOXML w:color.
  borderColor: z.string().regex(/^#?[0-9a-fA-F]{6}$/).describe('Couleur du trait : "000000" ou "#000000".').optional(),
}).strict();
const WordSetPageLayoutSchema = SetPageLayoutInputSchema;
const WordCopyFormatSchema = z.object({ action: z.literal('copy_format'), from: index, target: z.discriminatedUnion('kind', [indices, range]) }).strict();
const WordInspectFormatSchema = z.object({ action: z.literal('inspect_format'), target: WordParagraphTargetSchema.optional() }).strict();

/** Raw objects also produce the LLM JSON schema; one refinement guards both wire endpoints. */
export const WORD_FORMATTING_SCHEMAS = {
  convert_list: WordConvertListSchema,
  attach_numbering: WordAttachNumberingSchema,
  edit_table: WordEditTableSchema,
  format_text: WordFormatTextSchema,
  format_paragraphs: WordFormatParagraphsSchema,
  set_page_layout: WordSetPageLayoutSchema,
  copy_format: WordCopyFormatSchema,
  inspect_format: WordInspectFormatSchema,
} as const;

export const WordFormattingActionSchema = z.discriminatedUnion('action', [WordFormatTextSchema, WordFormatParagraphsSchema, WordSetPageLayoutSchema, WordCopyFormatSchema, WordInspectFormatSchema, WordConvertListSchema, WordAttachNumberingSchema, WordEditTableSchema]).superRefine((action, ctx) => {
  if (action.action === 'convert_list' || action.action === 'attach_numbering') {
    if ((action.to ?? action.from) < action.from) ctx.addIssue({code:z.ZodIssueCode.custom,message:'to must be >= from'});
    if (action.action === 'convert_list' && action.kind === 'bullet' && action.startAt !== undefined) ctx.addIssue({code:z.ZodIssueCode.custom,message:'startAt applies only to ordered lists'});
    if (action.action === 'attach_numbering' && action.attachTo >= action.from && action.attachTo <= (action.to ?? action.from)) ctx.addIssue({code:z.ZodIssueCode.custom,message:'attachTo must be outside the target range'});
  }
  if (action.action === 'edit_table') {
    const insertion = action.operation.startsWith('insert');
    if (!insertion && (action.count !== undefined || action.position !== undefined || action.texts !== undefined)) ctx.addIssue({code:z.ZodIssueCode.custom,message:'delete operations take only table_index and index'});
    if (action.operation === 'insert_row' && action.position !== 'above' && action.position !== 'below') ctx.addIssue({code:z.ZodIssueCode.custom,message:'insert_row requires position above or below'});
    if (action.operation === 'insert_column' && action.position !== 'left' && action.position !== 'right') ctx.addIssue({code:z.ZodIssueCode.custom,message:'insert_column requires position left or right'});
    if (action.texts !== undefined && (!insertion || (action.count ?? 1) !== 1)) ctx.addIssue({code:z.ZodIssueCode.custom,message:'texts can fill one inserted row or column only'});
  }
  if ('target' in action && action.target?.kind === 'range' && action.target.from > action.target.to) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['target'], message: 'target.from must be <= target.to; use indices from a recent Word read' });
  }
  if (action.action === 'format_text' || action.action === 'format_paragraphs') {
    if (!Object.entries(action).some(([key, value]) => key !== 'action' && key !== 'target' && key !== 'onlyWhere' && value !== undefined)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${action.action} requires at least one formatting setting` });
    }
  }
  if (action.action === 'format_paragraphs' && action.reset && Object.entries(action).some(([key, value]) => !['action', 'target', 'reset'].includes(key) && value !== undefined)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reset'], message: 'reset must be used alone; apply explicit settings in a later call' });
  }
  if (action.action === 'format_paragraphs' && (action.borderWidthPt !== undefined || action.borderSpacePt !== undefined || action.borderColor !== undefined) && (!action.border || action.border === 'none')) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['border'], message: 'borderWidthPt, borderSpacePt and borderColor require a visible border, such as bottom or box' });
  }
  if (action.action === 'set_page_layout' && action.orientation === undefined && action.paperSize === undefined && !Object.values(action.marginsCm ?? {}).some((value) => value !== undefined)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'set_page_layout requires orientation, paperSize or at least one marginsCm side' });
  }
});
/** Word-only nested values a model may serialize as JSON strings; Canvas keys are repaired by {@link coerceCanvasToolArgs}. */
const WORD_STRUCTURAL_KEYS = new Set(['in', 'paragraphIndices', 'marginsCm']);

/**
 * Repairs tool-call arguments whose nested objects or arrays arrived as JSON strings
 * (`target: "{\"kind\":\"all\"}"`), the Canvas incident class, before the Word
 * formatting schema reads them. Text values are never parsed.
 */
export function coerceWordFormattingArgs(args: unknown): unknown {
  const repair = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(repair);
    if (value === null || typeof value !== 'object') return value;
    return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([key, entry]) => {
      if (WORD_STRUCTURAL_KEYS.has(key) && typeof entry === 'string' && /^\s*[[{]/.test(entry)) {
        try { return [key, repair(JSON.parse(entry) as unknown)]; } catch { return [key, entry]; }
      }
      return [key, repair(entry)];
    }));
  };
  return repair(coerceCanvasToolArgs(args));
}

export type WordFormatTextAction = z.infer<typeof WordFormatTextSchema>;
export type WordFormatParagraphsAction = z.infer<typeof WordFormatParagraphsSchema>;
export type WordSetPageLayoutAction = z.infer<typeof WordSetPageLayoutSchema>;
export type WordCopyFormatAction = z.infer<typeof WordCopyFormatSchema>;
export type WordFormattingAction = z.infer<typeof WordFormattingActionSchema>;
